import { assertSchemaInput } from '@autone/utils';
import * as z from 'zod';

import { type GetAssortmentProductLocationsResponse } from '../types';

const pageInfoSchema = assertSchemaInput<
  GetAssortmentProductLocationsResponse['page_info']
>()(z.object({ total_count: z.number(), next_cursor: z.string().nullable() }));

const itemLocationSchema = assertSchemaInput<
  GetAssortmentProductLocationsResponse['items'][number]['location']
>()(
  z.object({
    id: z.string(),
    desc: z.string(),
    country_id: z.string().nullable(),
    country_desc: z.string().nullable(),
    region_id: z.string().nullable(),
    region_desc: z.string().nullable(),
  }),
);

const skuCountsSchema = assertSchemaInput<
  GetAssortmentProductLocationsResponse['items'][number]['sku_counts']
>()(
  z
    .object({ assorted: z.number(), total: z.number() })
    .transform((obj) => ({ ...obj, awaiting_requests: 0, is_disabled: false })),
);

const itemSchema = assertSchemaInput<
  GetAssortmentProductLocationsResponse['items'][number]
>()(
  z
    .object({
      location: itemLocationSchema,
      sku_counts: skuCountsSchema,
      location_cluster: z.string().nullable(),
      is_assorted: z.boolean(),
      assorted_start_date: z.string().nullable(),
      assorted_end_date: z.string().nullable(),
      initial_allocation: z.number().nullable(),
      first_sale_date: z.string().nullable(),
      latest_sale_date: z.string().nullable(),
      lifetime_sales_units: z.number(),
      lifetime_sales_value: z.number(),
      inventory_units: z.number(),
      is_managed_by_user: z.boolean().optional(),
      parent_location_inventory_units: z.number().nullable().optional(),
      pending_from_production_units: z.number().nullable(),
      parent_location_pending_from_production_units: z
        .number()
        .nullable()
        .optional(),
    })
    .transform(({ initial_allocation, ...rest }) => ({
      ...rest,
      initial_allocation: {
        value: initial_allocation,
        awaiting_requests: 0,
        is_disabled: false,
      },
    })),
);

export const getAssortmentProductLocationsSchema =
  assertSchemaInput<GetAssortmentProductLocationsResponse>()(
    z.object({
      page_info: pageInfoSchema,
      items: z.array(itemSchema),
    }),
  );

export type TransformedAssortmentProductLocationsResponse = z.infer<
  typeof getAssortmentProductLocationsSchema
>;

export type TransformedAssortmentProductLocationsItem =
  TransformedAssortmentProductLocationsResponse['items'][number];
